/**
 * global.tsv(long format) → {@link GlobalDataRepository} 싱글톤 — global 설정값의 런타임 접근 SSoT.
 *
 * 수치 수정은 `data/global.tsv`에서 → `npm run export:data`(tools/export-global.mjs가 raw rows emit).
 * core.ts/field.ts는 `globalRepo.getInt(K.초기골드)`처럼 조회한다. 키 상수 {@link K}가 오타를 컴파일 에러로 차단.
 */
import { GlobalDataRepository, type GlobalDataRow, type GlobalSchema } from "@yjsgames/data-repository";
import rows from "./generated/global.json";

const repo = new GlobalDataRepository();
repo.addData(rows as readonly GlobalDataRow[]);

/** global 설정값 저장소 싱글톤. */
export const globalRepo: GlobalDataRepository = repo;

/**
 * global.tsv 키 상수 — `globalRepo.getX(K.key)` 우변에서 오타를 컴파일 타임에 차단한다.
 * (repo 자체는 string 키라 K 우회 시 런타임 throw — globalDataRepository.test가 커버.)
 */
export const K = {
  initialGold: "initialGold",
  initialDiamond: "initialDiamond",
  defaultSpaceshipId: "defaultSpaceshipId",
  soundMasterGain: "soundMasterGain",
  baseDamage: "baseDamage",
  baseProjectileSpeed: "baseProjectileSpeed",
  fireIntervalMs: "fireIntervalMs",
  brickBaseHp: "brickBaseHp",
  brickHpGrowth: "brickHpGrowth",
  brickBaseGold: "brickBaseGold",
  brickGoldGrowth: "brickGoldGrowth",
  moduleLevelDamageBonus: "moduleLevelDamageBonus",
  moduleMasteryActivationXp: "moduleMasteryActivationXp",
  moduleMasteryHitXp: "moduleMasteryHitXp",
  moduleMasteryKillXp: "moduleMasteryKillXp",
  permanentDamagePerPoint: "permanentDamagePerPoint",
  rebirthRequiredChapter: "rebirthRequiredChapter",
  explosionBaseRadius: "explosionBaseRadius",
  explosionRadiusPerLevel: "explosionRadiusPerLevel",
  dungeonTimeLimitSec: "dungeonTimeLimitSec",
  battlefieldPressureDropIntervalMs: "battlefieldPressureDropIntervalMs",
  battlefieldPressureDropRows: "battlefieldPressureDropRows",
  battlefieldPressureWarningRows: "battlefieldPressureWarningRows",
  battlefieldPressureDangerRows: "battlefieldPressureDangerRows",
  starterChapterMax: "starterChapterMax",
  starterChapterRampEnd: "starterChapterRampEnd",
  starterChapterBrickHpMultiplier: "starterChapterBrickHpMultiplier",
  starterChapterMinRows: "starterChapterMinRows",
  starterChapterMaxRows: "starterChapterMaxRows",
  starterChapterDensity: "starterChapterDensity",
  starterChapterHpBudget: "starterChapterHpBudget",
  worldChapterSpan: "worldChapterSpan",
  brickHpWorldMultiplierExpr: "brickHpWorldMultiplierExpr",
  brickGoldWorldMultiplierExpr: "brickGoldWorldMultiplierExpr",
  burstDelayMs: "burstDelayMs",
  critBaseChance: "critBaseChance",
  critChancePerLevel: "critChancePerLevel",
  critMultiplier: "critMultiplier",
  greedBaseMultiplier: "greedBaseMultiplier",
  greedPerLevel: "greedPerLevel",
  speedModuleBase: "speedModuleBase",
  speedModulePerLevel: "speedModulePerLevel",
  rarityColorCommon: "rarityColorCommon",
  rarityColorRare: "rarityColorRare",
  rarityColorEpic: "rarityColorEpic",
  rarityColorLegendary: "rarityColorLegendary",
  rarityColorTranscendent: "rarityColorTranscendent",
  goldBrickChance: "goldBrickChance",
  goldBrickMaxPerChapter: "goldBrickMaxPerChapter",
  goldBrickRewardMultiplier: "goldBrickRewardMultiplier",
  goldBrickTint: "goldBrickTint",
  rebirthChapterExpr: "rebirthChapterExpr",
  rebirthPointExpr: "rebirthPointExpr",
  blockWeight1x1: "blockWeight1x1",
  blockWeight2x1: "blockWeight2x1",
  blockWeight1x2: "blockWeight1x2",
  blockWeight2x2: "blockWeight2x2",
  melodyEvery: "melodyEvery",
  melodyHpBudget: "melodyHpBudget",
  melodyStaggerMs: "melodyStaggerMs",
  critText: "critText",
  critTextColor: "critTextColor",
  critExplosionRadiusMul: "critExplosionRadiusMul",
  critExplosionColor: "critExplosionColor",
  critPierceBonus: "critPierceBonus",
  critChainBonus: "critChainBonus",
  critSplitBonus: "critSplitBonus",
  critGreedMul: "critGreedMul",
  launcherShapeId: "launcherShapeId",
  launcherEffect: "launcherEffect",
  fusionDiamondCost: "fusionDiamondCost",
  mysteryBrickChance: "mysteryBrickChance",
  mysteryBrickMaxPerChapter: "mysteryBrickMaxPerChapter",
  mysteryBrickTint: "mysteryBrickTint",
  mysteryMulValues: "mysteryMulValues",
  mysteryMulWeights: "mysteryMulWeights",
  mysteryDiamondChance: "mysteryDiamondChance",
  mysteryDiamondValues: "mysteryDiamondValues",
  mysteryDiamondWeights: "mysteryDiamondWeights",
  mysteryDiamondDailyLimit: "mysteryDiamondDailyLimit",
  mysteryRelicChance: "mysteryRelicChance",
  mysteryRelicDailyLimit: "mysteryRelicDailyLimit",
  mysteryToastColor: "mysteryToastColor",
  gridUnlockBaseCost: "gridUnlockBaseCost",
  gridUnlockCostMultiplier: "gridUnlockCostMultiplier",
  brickComboWindowMs: "brickComboWindowMs",
  brickComboSemitoneStep: "brickComboSemitoneStep",
  brickComboMaxSemitone: "brickComboMaxSemitone",
  // 벽돌 피격 반동 + 크리티컬 파괴 임팩트 번들
  impactShakeScale: "impactShakeScale",
  impactShakeCritScale: "impactShakeCritScale",
  critShatterEnergyMul: "critShatterEnergyMul",
  critSparkScale: "critSparkScale",
  critDamageTextSize: "critDamageTextSize",
} as const;

/**
 * 적재 검증 스키마 — 키 누락·타입(맵 분류)·범위를 `globalRepo.validate(GLOBAL_SCHEMA)`로 점검.
 * `dataIntegrity.test.ts`가 `.ok===true`를 단언(long TSV 변환 시 키 누락/오타의 안전망).
 */
export const GLOBAL_SCHEMA: GlobalSchema = {
  initialGold: { type: "int", min: 0 },
  initialDiamond: { type: "int", min: 0 },
  defaultSpaceshipId: { type: "string" },
  soundMasterGain: { type: "float", min: 0 },
  baseDamage: { type: "int", min: 1 },
  baseProjectileSpeed: { type: "int", min: 1 },
  fireIntervalMs: { type: "int", min: 1 },
  brickBaseHp: { type: "int", min: 1 },
  brickHpGrowth: { type: "float", min: 1 },
  brickBaseGold: { type: "int", min: 0 },
  brickGoldGrowth: { type: "float", min: 1 },
  moduleLevelDamageBonus: { type: "float", min: 0 },
  moduleMasteryActivationXp: { type: "int", min: 0 },
  moduleMasteryHitXp: { type: "int", min: 0 },
  moduleMasteryKillXp: { type: "int", min: 0 },
  permanentDamagePerPoint: { type: "float", min: 0 },
  rebirthRequiredChapter: { type: "int", min: 0 },
  explosionBaseRadius: { type: "int", min: 0 },
  explosionRadiusPerLevel: { type: "int", min: 0 },
  dungeonTimeLimitSec: { type: "int", min: 1 },
  battlefieldPressureDropIntervalMs: { type: "int", min: 1000 },
  battlefieldPressureDropRows: { type: "int", min: 1 },
  battlefieldPressureWarningRows: { type: "int", min: 1 },
  battlefieldPressureDangerRows: { type: "int", min: 1 },
  starterChapterMax: { type: "int", min: 1 },
  starterChapterRampEnd: { type: "int", min: 1 },
  starterChapterBrickHpMultiplier: { type: "float", min: 0, max: 1 },
  starterChapterMinRows: { type: "int", min: 1 },
  starterChapterMaxRows: { type: "int", min: 1 },
  starterChapterDensity: { type: "float", min: 0, max: 1 },
  starterChapterHpBudget: { type: "int", min: 1 },
  worldChapterSpan: { type: "int", min: 1 },
  brickHpWorldMultiplierExpr: { type: "string" },
  brickGoldWorldMultiplierExpr: { type: "string" },
  burstDelayMs: { type: "int", min: 0 },
  critBaseChance: { type: "float", min: 0, max: 1 },
  critChancePerLevel: { type: "float", min: 0, max: 1 },
  critMultiplier: { type: "float", min: 1 },
  greedBaseMultiplier: { type: "float", min: 0 },
  greedPerLevel: { type: "float", min: 0 },
  speedModuleBase: { type: "float", min: 0 },
  speedModulePerLevel: { type: "float", min: 0 },
  rarityColorCommon: { type: "string" },
  rarityColorRare: { type: "string" },
  rarityColorEpic: { type: "string" },
  rarityColorLegendary: { type: "string" },
  rarityColorTranscendent: { type: "string" },
  goldBrickChance: { type: "float", min: 0, max: 1 },
  goldBrickMaxPerChapter: { type: "int", min: 0 },
  goldBrickRewardMultiplier: { type: "float", min: 1 },
  goldBrickTint: { type: "string" },
  rebirthChapterExpr: { type: "string" },
  rebirthPointExpr: { type: "string" },
  blockWeight1x1: { type: "float", min: 0 },
  blockWeight2x1: { type: "float", min: 0 },
  blockWeight1x2: { type: "float", min: 0 },
  blockWeight2x2: { type: "float", min: 0 },
  melodyEvery: { type: "int", min: 1 },
  melodyHpBudget: { type: "int", min: 0 },
  melodyStaggerMs: { type: "int", min: 0 },
  critText: { type: "string" },
  critTextColor: { type: "string" },
  critExplosionRadiusMul: { type: "float", min: 0 },
  critExplosionColor: { type: "string" },
  critPierceBonus: { type: "int", min: 0 },
  critChainBonus: { type: "int", min: 0 },
  critSplitBonus: { type: "int", min: 0 },
  critGreedMul: { type: "float", min: 0 },
  fusionDiamondCost: { type: "int", min: 0 },
  mysteryBrickChance: { type: "float", min: 0, max: 1 },
  mysteryBrickMaxPerChapter: { type: "int", min: 0 },
  mysteryBrickTint: { type: "string" },
  mysteryMulValues: { type: "intArray" },
  mysteryMulWeights: { type: "floatArray" },
  mysteryDiamondChance: { type: "float", min: 0, max: 1 },
  mysteryDiamondValues: { type: "intArray" },
  mysteryDiamondWeights: { type: "floatArray" },
  mysteryDiamondDailyLimit: { type: "int", min: 0 },
  mysteryRelicChance: { type: "float", min: 0, max: 1 },
  mysteryRelicDailyLimit: { type: "int", min: 0 },
  mysteryToastColor: { type: "string" },
  gridUnlockBaseCost: { type: "int", min: 0 },
  gridUnlockCostMultiplier: { type: "int", min: 1 },
  brickComboWindowMs: { type: "int", min: 0 },
  brickComboSemitoneStep: { type: "int", min: 0 },
  brickComboMaxSemitone: { type: "int", min: 0 },
  // 벽돌 피격 반동 + 크리티컬 파괴 임팩트 번들 (max = tsv 오타로 인한 극단값 가드 — 화면 밖 분출/과흔들림 방지)
  impactShakeScale: { type: "float", min: 0, max: 2 },
  impactShakeCritScale: { type: "float", min: 0, max: 3 },
  critShatterEnergyMul: { type: "float", min: 0, max: 5 },
  critSparkScale: { type: "float", min: 0, max: 5 },
  critDamageTextSize: { type: "string" },
} as const;
